import { Box, Drawer, Stack } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import Scrollbar from "components/Scrollbar";
import SupplierLogo from "components/SupplierLogo";
import { NAVBAR } from "config";
import { RootState, useSelector } from "redux/store";
import useCollapseDrawer from "hooks/useCollapseDrawer";
import useResponsive from "hooks/useResponsive";
import NavSectionVertical from "layouts/vertical";
import { useEffect } from "react";
import cssStyles from "utils/cssStyles";
import CollapseButton from "./CollapseButton";
import NavbarAccount from "./NavbarAccount";
import menuBar from "./navConfig";
import useLocales from "hooks/useLocales";
import { CustomTheme } from "enums/customTheme";

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    transition: theme.transitions.create("width", {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

type Props = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
};

export default function NavbarVertical({
  isOpenSidebar,
  onCloseSidebar,
}: Props) {
  const { translate } = useLocales();
  const theme = useTheme();
  const isDesktop = useResponsive("up", "lg");
  const { isCollapse, collapseClick, collapseHover, onToggleCollapse } =
    useCollapseDrawer();
  const customTheme = useSelector(
    (state: RootState) => state.supplier.supplier?.customTheme,
  );

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Scrollbar
        sx={{
          flexGrow: 1,
          height: 1,
          "& .simplebar-content": {
            height: 1,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Stack
          spacing={0.5}
          sx={{
            gap: 2,
            flexShrink: 0,
            padding: 3,
            pb: 4,
            overflowX: "hidden",
            ...(isCollapse && { alignItems: "center" }),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-end" }}>
            <SupplierLogo isCollapse={isCollapse} isDrawer />
          </Box>
          <NavSectionVertical
            navConfig={menuBar().navConfig}
            isCollapse={isCollapse}
          />
        </Stack>
      </Scrollbar>

      <Box
        sx={{
          flexShrink: 0,
          p: 3,
          pt: 1,
          bgcolor: "background.default",
        }}
      >
        <NavbarAccount isCollapse={isCollapse} />
      </Box>
      {isDesktop && (
        <Box
          sx={{
            position: "absolute",
            top: 20,
            right: -20,
            zIndex: 9999,
          }}
        >
          <CollapseButton
            theme={theme}
            onToggleCollapse={onToggleCollapse}
            collapseClick={collapseClick}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse
            ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH
            : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: "absolute",
        }),
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: NAVBAR.DASHBOARD_WIDTH, top: 0, height: "100%" },
          }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              overflow: "visible",
              borderRight: `1px solid ${theme.palette.text.borderLine}`,
              ...(customTheme === CustomTheme.Sage && {
                borderRight: `4px solid ${theme.palette.sage_green}`,
              }),
              width: NAVBAR.DASHBOARD_WIDTH,
              bgcolor: "background.default",
              transition: (theme) =>
                theme.transitions.create("width", {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
